import { Container, type ContainerOptions } from "../core/Container.js";
import type { DragPayload, KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ItemSlot } from "./ItemSlot.js";
import { directionOfKey, stepInGrid } from "./slotNavigation.js";
import {
  ITEM_DRAG_KIND,
  canStack,
  mergeStacks,
  type ItemDragData,
  type ItemStack,
  type RarityTable,
} from "./items.js";

export interface InventoryGridOptions extends ContainerOptions {
  columns?: number;
  rows?: number;
  /** Side of one square cell, in px. */
  cellSize?: number;
  /** Gap between cells. */
  gap?: number;
  /** Starting contents, by slot index. Shorter arrays leave the rest empty. */
  items?: readonly (ItemStack | null)[];
  rarities?: RarityTable;
  /** Take items dragged in from elsewhere. */
  acceptsExternal?: boolean;
  onChange?: () => void;
  onActivate?: (item: ItemStack, index: number) => void;
}

/**
 * A grid of `ItemSlot`s that moves, swaps and stacks what is dragged around
 * inside it.
 *
 * The grid owns the contents; the slots are a view of them. That is what
 * makes a move atomic: a drop works out the whole before-and-after of both
 * ends and writes it in one go, so a swap can never leave a copy of an item
 * in the slot it came from — which is what happens if each slot edits itself.
 *
 * A drag that ends somewhere else entirely, or is canceled with Escape,
 * changes nothing. Only the drop moves anything.
 */
export class InventoryGrid extends Container {
  #columns: number;
  #rows: number;
  #items: (ItemStack | null)[];
  #slots: ItemSlot[] = [];
  /** The cell the keyboard is on. */
  #cursor = 0;
  /**
   * Whether the cursor has been *used* yet, as opposed to merely focused.
   *
   * Activating a window focuses its first tab stop, which is this grid — so
   * without this a window opening would ring cell zero, and the ring reads as
   * a selection the user never made. The browsers' `:focus-visible` draws the
   * same distinction for the same reason. Set by the first key or press that
   * engages the cursor, and cleared when focus leaves.
   */
  #engaged = false;

  cellSize: number;
  gap: number;
  rarities: RarityTable | undefined;
  acceptsExternal: boolean;

  constructor(options: InventoryGridOptions = {}) {
    const columns = Math.max(1, Math.floor(options.columns ?? 6));
    const rows = Math.max(1, Math.floor(options.rows ?? 4));
    const cellSize = options.cellSize ?? 44;
    const gap = options.gap ?? 4;
    super({
      // One tab stop for the whole grid, not one per cell. Twenty-four cells
      // in the tab order means twenty-four presses to get past a bag, and it
      // puts a focus ring on cell zero the moment the window opens, which
      // reads as a selection nobody made.
      tabStop: true,
      width: columns * cellSize + (columns - 1) * gap,
      height: rows * cellSize + (rows - 1) * gap,
      ...options,
    });
    this.#columns = columns;
    this.#rows = rows;
    this.cellSize = cellSize;
    this.gap = gap;
    this.rarities = options.rarities;
    this.acceptsExternal = options.acceptsExternal ?? true;

    this.#items = new Array<ItemStack | null>(columns * rows).fill(null);
    (options.items ?? []).forEach((item, i) => {
      if (i < this.#items.length) this.#items[i] = item ?? null;
    });

    this.#build();
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("focus", () => this.#syncCursor());
    this.on("blur", () => {
      this.#engaged = false;
      this.#syncCursor();
    });
    if (options.onChange) this.on("change", options.onChange);
    if (options.onActivate) {
      this.on("itemactivate", (index) => {
        const item = this.#items[index];
        if (item) options.onActivate!(item, index);
      });
    }
  }

  get columns(): number {
    return this.#columns;
  }

  get rows(): number {
    return this.#rows;
  }

  get capacity(): number {
    return this.#items.length;
  }

  /** The contents, in slot order. A copy: write through `setItem`. */
  get items(): readonly (ItemStack | null)[] {
    return [...this.#items];
  }

  get slots(): readonly ItemSlot[] {
    return this.#slots;
  }

  itemAt(index: number): ItemStack | null {
    return this.#items[index] ?? null;
  }

  // --- keyboard cursor -----------------------------------------------------

  /** The cell the keyboard is on. */
  get cursorIndex(): number {
    return this.#cursor;
  }
  set cursorIndex(index: number) {
    const next = Math.min(Math.max(Math.floor(index), 0), Math.max(0, this.#items.length - 1));
    if (next === this.#cursor) return;
    this.#cursor = next;
    this.#syncCursor();
    this.emit("selectionchanged");
  }

  /** The slot the keyboard is on. */
  get cursorSlot(): ItemSlot | null {
    return this.#slots[this.#cursor] ?? null;
  }

  /**
   * Mark the cursor cell — only while the grid has focus, and only once the
   * cursor has actually been engaged.
   *
   * Both halves matter. Without the focus test an unfocused grid keeps a ring;
   * without the engaged test a window merely opening puts one on cell zero,
   * which is the thing this is here to prevent.
   */
  #syncCursor(): void {
    const show = this.focused && this.#engaged;
    this.#slots.forEach((slot, i) => {
      const current = show && i === this.#cursor;
      if (slot.current === current) return;
      slot.current = current;
      slot.invalidate();
    });
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    const direction = directionOfKey(e.key);
    if (direction) {
      // The first arrow shows the cursor where it already was rather than
      // moving it, so nothing is skipped over on the way in.
      const wake = this.#engage();
      if (!wake) {
        this.cursorIndex = stepInGrid(this.#cursor, this.#items.length, this.#columns, direction);
      }
      e.handled = true;
      return;
    }
    switch (e.key) {
      case "Home":
        this.#engage();
        this.cursorIndex = 0;
        e.handled = true;
        break;
      case "End":
        this.#engage();
        this.cursorIndex = this.#items.length - 1;
        e.handled = true;
        break;
      case "Enter":
      case " ":
        this.#engage();
        this.#slots[this.#cursor]?.activate();
        e.handled = true;
        break;
      default:
        break;
    }
  }

  /**
   * Show the cursor from here on. Returns true when this was the call that
   * woke it, so a caller can leave it where it is rather than moving it.
   */
  #engage(): boolean {
    if (this.#engaged) return false;
    this.#engaged = true;
    this.#syncCursor();
    return true;
  }

  /** A press anywhere in the grid focuses it and takes the cursor with it. */
  #onPointerDown(e: MouseEventArgs): void {
    this.focus();
    this.#engage();
    const index = this.indexAt(e.x, e.y);
    if (index >= 0) this.cursorIndex = index;
  }

  setItem(index: number, item: ItemStack | null): void {
    if (index < 0 || index >= this.#items.length) return;
    this.#items[index] = item;
    const slot = this.#slots[index];
    if (slot) slot.item = item;
    this.emit("change");
    this.invalidate();
  }

  /** The first empty slot, or -1. */
  firstEmpty(): number {
    return this.#items.indexOf(null);
  }

  /**
   * Put `item` somewhere, merging into matching stacks first.
   *
   * Returns whatever would not fit, or null when it all went in. Filling
   * partial stacks before opening a new slot is what every game does, and
   * doing it the other way round leaves an inventory that looks full and is
   * not.
   */
  add(item: ItemStack): ItemStack | null {
    let left: ItemStack | null = item;
    for (let i = 0; i < this.#items.length && left; i++) {
      const here = this.#items[i] ?? null;
      if (!canStack(here, left)) continue;
      const merged = mergeStacks(here!, left);
      this.#items[i] = merged.into;
      this.#slots[i]!.item = merged.into;
      left = merged.from;
    }
    while (left) {
      const empty = this.firstEmpty();
      if (empty < 0) break;
      this.setItem(empty, left);
      left = null;
    }
    this.emit("change");
    this.invalidate();
    return left;
  }

  /** Empty every slot. */
  clear(): void {
    this.#items.fill(null);
    for (const slot of this.#slots) slot.item = null;
    this.emit("change");
    this.invalidate();
  }

  /**
   * Move what is in `from` to `to`, in one write.
   *
   * Stacks merge when they can, and swap when they cannot. `from` may be -1
   * for an item arriving from outside the grid, in which case `incoming` says
   * what it is.
   */
  moveTo(from: number, to: number, incoming?: ItemStack | null): boolean {
    if (to < 0 || to >= this.#items.length) return false;
    const source = from >= 0 ? this.#items[from] ?? null : incoming ?? null;
    if (!source) return false;
    if (from === to) return false;

    const target = this.#items[to] ?? null;

    if (canStack(target, source)) {
      const merged = mergeStacks(target!, source);
      this.#write(to, merged.into);
      // Whatever would not fit stays where it came from.
      if (from >= 0) this.#write(from, merged.from);
      this.emit("change");
      this.invalidate();
      return true;
    }

    this.#write(to, source);
    if (from >= 0) this.#write(from, target);
    this.emit("change");
    this.invalidate();
    return true;
  }

  #write(index: number, item: ItemStack | null): void {
    this.#items[index] = item;
    const slot = this.#slots[index];
    if (slot) slot.item = item;
  }

  // --- drops ---------------------------------------------------------------

  /**
   * The grid takes a drop that its slots declined — the gap between cells,
   * and a locked slot with the rest of the grid open behind it. Which slot it
   * lands in is worked out from where the pointer was.
   */
  override canAcceptDrop(payload: DragPayload): boolean {
    if (payload.kind !== ITEM_DRAG_KIND) return false;
    return this.acceptsExternal || this.#ownsSource(payload);
  }

  protected override onDrop(payload: DragPayload, p: { x: number; y: number }): boolean {
    const index = this.indexAt(p.x, p.y);
    if (index < 0) return false;
    return this.#take(payload, index);
  }

  /** Called by a slot of this grid when something is dropped on it. */
  acceptInto(payload: DragPayload, index: number): boolean {
    return this.#take(payload, index);
  }

  #take(payload: DragPayload, index: number): boolean {
    const data = payload.data as ItemDragData | undefined;
    if (!data?.item) return false;
    // A move inside this grid is one write, both ends at once.
    if (this.#ownsSource(payload)) return this.moveTo(data.fromIndex, index, data.item);

    if (!this.acceptsExternal || index < 0 || index >= this.#items.length) return false;
    // From another container. This end can only write itself, so it takes the
    // item and leaves what the source should keep on the payload.
    const target = this.#items[index] ?? null;
    if (canStack(target, data.item)) {
      const merged = mergeStacks(target!, data.item);
      this.#write(index, merged.into);
      data.remainder = merged.from;
    } else {
      this.#write(index, data.item);
      // Swapping across containers: what was here goes back the other way.
      data.remainder = target;
    }
    data.takenFromSource = true;
    this.emit("change");
    this.invalidate();
    return true;
  }

  #ownsSource(payload: DragPayload): boolean {
    return payload.source !== null && this.contains(payload.source);
  }

  /** The slot index containing local point (x, y), or -1. */
  indexAt(x: number, y: number): number {
    const stride = this.cellSize + this.gap;
    const column = Math.floor(x / stride);
    const row = Math.floor(y / stride);
    if (column < 0 || column >= this.#columns || row < 0 || row >= this.#rows) return -1;
    return row * this.#columns + column;
  }

  // --- layout --------------------------------------------------------------

  #build(): void {
    this.controls.clear();
    this.#slots = [];
    for (let i = 0; i < this.#items.length; i++) {
      const slot = new ItemSlot({
        item: this.#items[i] ?? null,
        index: i,
        rarities: this.rarities,
        width: this.cellSize,
        height: this.cellSize,
        // The grid is the tab stop; the cells are what the cursor moves over.
        tabStop: false,
      });
      // The grid, not the slot, decides what a drop means: only it can see
      // both ends of the move and write them together.
      slot.on("drop", (e) => {
        e.accepted = this.acceptInto(e.payload, slot.index);
      });
      slot.on("itemactivate", () => this.emit("itemactivate", slot.index));
      // Settling a move that went to another container: this end still has
      // the item until it is told the far end took it.
      slot.on("dragend", (e) => {
        const data = e.payload.data as ItemDragData | undefined;
        if (!e.accepted || !data?.takenFromSource) return;
        this.setItem(slot.index, data.remainder ?? null);
      });
      this.#slots.push(slot);
      this.controls.add(slot);
    }
    this.#layout();
    this.#syncCursor();
  }

  /** Resize the grid, keeping whatever contents still fit. */
  setSizeInCells(columns: number, rows: number): void {
    const nextColumns = Math.max(1, Math.floor(columns));
    const nextRows = Math.max(1, Math.floor(rows));
    if (nextColumns === this.#columns && nextRows === this.#rows) return;
    const kept = this.#items.slice(0, nextColumns * nextRows);
    this.#columns = nextColumns;
    this.#rows = nextRows;
    this.#items = new Array<ItemStack | null>(nextColumns * nextRows).fill(null);
    kept.forEach((item, i) => {
      this.#items[i] = item;
    });
    this.setSize(
      nextColumns * this.cellSize + (nextColumns - 1) * this.gap,
      nextRows * this.cellSize + (nextRows - 1) * this.gap,
    );
    this.#build();
  }

  #layout(): void {
    const stride = this.cellSize + this.gap;
    this.#slots.forEach((slot, i) => {
      const column = i % this.#columns;
      const row = Math.floor(i / this.#columns);
      slot.setBounds(column * stride, row * stride, this.cellSize, this.cellSize);
    });
  }

  protected override onPaint(_rc: RenderContext, _theme: Theme): void {
    // Nothing of its own: the slots are the grid. A background belongs to
    // whatever panel the grid was put in.
  }
}
